import clsx from "clsx";
import { BarChart2, Plus, Share2, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../services/api";
import { styles } from "../../styles";
import ContextualChart from "../charts/ContextualChart";
import Skeleton from "../ui/Skeleton";
import StatusPill from "../ui/StatusPill";
import ShareTradeCard from "./ShareTradeCard";

const Trades = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [symbolFilter, setSymbolFilter] = useState(searchParams.get("symbol") || "");
  const [trades, setTrades] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [sort, setSort] = useState({
    orderBy: "entry_time",
    order: "DESC",
  });
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [sharingTrade, setSharingTrade] = useState(null);
  const [deletingTrade, setDeletingTrade] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [formData, setFormData] = useState({
    symbol: "",
    side: "BUY",
    quantity: "",
    entryPrice: "",
    stopLoss: "",
    takeProfit: "",
    strategy: "",
    exitStrategy: "single",
    closeImmediately: false,
    exitPrice: "",
    exitTime: "",
  });
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    const fromUrl = searchParams.get("symbol") || "";
    setSymbolFilter(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    fetchTrades();
  }, [symbolFilter]);

  const clearSymbolFilter = () => {
    setSymbolFilter("");
    setSearchParams({});
  };

  const fetchTrades = async (
    page = pagination.page,
    limit = pagination.limit,
    orderBy = sort.orderBy,
    order = sort.order
  ) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        orderBy,
        order,
      });
      if (symbolFilter) params.set("symbol", symbolFilter);
      const { data } = await api.get(`/trades?${params}`);
      if (data.success) {
        setTrades(data.data.trades || []);
        setPagination({
          page: page, // Use the requested page number, not from API response
          limit: limit,
          total: data.data.pagination.total,
          totalPages: Math.ceil(data.data.pagination.total / limit),
        });
      }
    } catch (error) {
      console.error("Failed to fetch trades:", error);
    } finally {
      // Small delay to prevent flashing if response is instant vs fast
      setTimeout(() => setLoading(false), 300);
    }
  };

  const handleSort = (column) => {
    const newOrder =
      sort.orderBy === column && sort.order === "ASC" ? "DESC" : "ASC";
    setSort({ orderBy: column, order: newOrder });
    fetchTrades(1, pagination.limit, column, newOrder);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (formData.closeImmediately && !formData.exitPrice) {
      setFormError("Enter an exit price, or uncheck \"Close this trade now\".");
      return;
    }

    try {
      const {
        closeImmediately,
        exitPrice,
        exitTime,
        ...entryFields
      } = formData;

      await api.post("/trades", {
        ...entryFields,
        quantity: parseFloat(formData.quantity),
        entryPrice: parseFloat(formData.entryPrice),
        stopLoss: formData.stopLoss ? parseFloat(formData.stopLoss) : null,
        takeProfit: formData.takeProfit
          ? parseFloat(formData.takeProfit)
          : null,
      });

      // The FIFO position engine matches trades purely by opposing
      // BUY/SELL executions on the same symbol — there's no "exit price"
      // field it reads. So "closing" a manually-entered trade means
      // recording a second, opposite-side execution, not editing the
      // first one. Without this, a manual entry with no matching offsetting
      // trade sits open forever with no way to reconcile it (see the SOFI
      // orphaned-trade incident).
      if (closeImmediately) {
        await api.post("/trades", {
          ...entryFields,
          side: formData.side === "BUY" ? "SELL" : "BUY",
          quantity: parseFloat(formData.quantity),
          entryPrice: parseFloat(exitPrice),
          entryTime: exitTime || undefined,
          stopLoss: null,
          takeProfit: null,
        });
      }

      // Positions are derived from trades and only recomputed on demand —
      // regroup now so the new trade(s) show up on the Positions page
      // immediately instead of appearing "missing" until the user
      // separately clicks "Create Positions from Trades".
      await api.post("/positions/create-from-trades");

      setShowModal(false);
      setFormData({
        symbol: "",
        side: "BUY",
        quantity: "",
        entryPrice: "",
        stopLoss: "",
        takeProfit: "",
        strategy: "",
        exitStrategy: "single",
        closeImmediately: false,
        exitPrice: "",
        exitTime: "",
      });
      fetchTrades();
    } catch (error) {
      console.error("Failed to create trade:", error);
      setFormError(
        error.response?.data?.error || "Failed to create trade. Please try again."
      );
    }
  };

  const confirmDeleteTrade = async () => {
    if (!deletingTrade) return;
    try {
      await api.delete(`/trades/${deletingTrade.id}`);
      setDeletingTrade(null);
      setDeleteError(null);
      fetchTrades();
    } catch (error) {
      console.error("Failed to delete trade:", error);
      setDeleteError("Failed to delete trade. Please try again.");
    }
  };

  const getStatusType = (status) => {
    switch (status) {
      case "closed":
        return "success"; // Or neutral if you prefer closed to be gray
      case "open":
        return "warning";
      default:
        return "neutral";
    }
  };

  const getPnlColor = (amount) => {
    if (!amount) return "";
    return amount >= 0 ? "text-success" : "text-danger";
  };

  const renderSortIcon = (column) => {
    if (sort.orderBy !== column) return <span style={{ opacity: 0.3 }}>⇅</span>;
    return sort.order === "ASC" ? "↑" : "↓";
  };

  // Skeleton Row Component for loading state
  const TableSkeletonRow = () => (
    <tr>
      <td style={{ padding: "0.75rem 1rem" }}>
        <Skeleton width="4rem" height="1.25rem" />
        <Skeleton width="6rem" height="0.75rem" style={{ marginTop: "0.5rem" }} />
      </td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="3rem" height="1.5rem" className="rounded-md" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="3rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="2.5rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="2rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="4rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="4rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="4rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="4rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="4rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="3.5rem" height="1.5rem" className="rounded-full" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="5rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="6rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="6rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="3rem" height="1rem" /></td>
      <td style={{ padding: "0.75rem 1rem" }}><Skeleton width="3rem" height="1rem" /></td>
    </tr>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <h1
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          Trades
        </h1>
        <button onClick={() => setShowModal(true)} style={styles.button}>
          <Plus size={20} />
          New Trade
        </button>
      </div>

      {symbolFilter && (
        <div className="flex items-center gap-2 mb-4 text-sm text-slate-300">
          <span>Filtering by symbol:</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg font-semibold">
            {symbolFilter}
            <button onClick={clearSymbolFilter} title="Clear filter" className="text-slate-400 hover:text-white">
              <X size={14} />
            </button>
          </span>
        </div>
      )}

      <div style={styles.card}>
        {!loading && trades.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <div style={{ fontSize: "48px", marginBottom: "1rem" }}>📈</div>
            <p style={{ color: "var(--text-secondary)", fontSize: "1.125rem" }}>
              {symbolFilter
                ? `No trades found for "${symbolFilter}".`
                : "No trades yet. Create your first trade to get started!"}
            </p>
          </div>
        ) : (
            <div className="trade-table-container">
              {/* Desktop Table */}
              <table className="trade-table hidden lg:table w-full">
                <thead>
                  <tr>
                    <th>
                      <button onClick={() => handleSort("symbol")}>
                        Symbol {renderSortIcon("symbol")}
                      </button>
                    </th>
                    <th>Asset Type</th>
                    <th>Cost</th>
                    <th>Side</th>
                    <th>Quantity</th>
                    <th>Entry Price</th>
                    <th>Exit Price</th>
                    <th>Stop Loss</th>
                    <th>Take Profit</th>
                    <th>Net P&L</th>
                    <th>Status</th>
                    <th>Strategy</th>
                    <th>
                      <button onClick={() => handleSort("entry_time")}>
                        Entry Time {renderSortIcon("entry_time")}
                      </button>
                    </th>
                    <th>
                      <button onClick={() => handleSort("exit_time")}>
                        Exit Time {renderSortIcon("exit_time")}
                      </button>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    // Render 5 Skeleton Rows while loading
                    Array.from({ length: 5 }).map((_, idx) => (
                      <TableSkeletonRow key={`skeleton-${idx}`} />
                    ))
                  ) : (
                    trades.map((trade) => (
                      <tr key={trade.id}>
                        <td>
                          <div className="trade-symbol">{trade.symbol}</div>
                          {trade.underlyingSymbol && (
                            <span className="trade-subtitle text-xs text-slate-500">
                              {trade.underlyingSymbol} {trade.optionType || ""}{" "}
                              {trade.strikePrice ? `$${trade.strikePrice}` : ""}{" "}
                              {trade.expirationDate
                                ? new Date(trade.expirationDate).toLocaleDateString()
                                : ""}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="px-2 py-1 bg-slate-800 text-slate-300 rounded-md text-xs font-semibold">
                            {trade.securityType}
                          </span>
                        </td>
                        <td>${trade.cost}</td>
                        <td>
                          <StatusPill 
                            type={trade.side === "BUY" ? "success" : "danger"}
                            className="font-medium"
                          >
                            {trade.side}
                          </StatusPill>
                        </td>
                        <td>
                          {trade.quantity}
                          {trade.multiplier && trade.multiplier !== 1 && (
                            <div className="text-xs text-slate-500">
                              (x{trade.multiplier})
                            </div>
                          )}
                        </td>
                        <td>${trade.entryPrice?.toFixed(2) || "N/A"}</td>
                        <td>${trade.exitPrice?.toFixed(2) || "N/A"}</td>
                        <td>${trade.stopLoss?.toFixed(2) || "N/A"}</td>
                        <td>${trade.takeProfit?.toFixed(2) || "N/A"}</td>
                        <td className={clsx("font-medium", getPnlColor(trade.netPnl))}>
                          {trade.netPnl ? `$${trade.netPnl.toFixed(2)}` : "Open"}
                        </td>
                        <td>
                          <StatusPill type={getStatusType(trade.status)}>
                            {trade.status?.toUpperCase() || "UNKNOWN"}
                          </StatusPill>
                        </td>
                        <td>{trade.strategy || "N/A"}</td>
                        <td>
                          {trade.entryTime
                            ? new Date(trade.entryTime).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>
                          {trade.exitTime
                            ? new Date(trade.exitTime).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>
                           <div className="flex items-center gap-2">
                             <button onClick={() => setSelectedTrade(trade)} className="p-2 bg-slate-800 text-slate-300 hover:text-emerald-400 hover:bg-slate-700 rounded-lg transition-colors" title="View Contextual Chart">
                                <BarChart2 size={16} />
                             </button>
                             <button onClick={() => setSharingTrade(trade)} className="p-2 bg-slate-800 text-slate-300 hover:text-indigo-400 hover:bg-slate-700 rounded-lg transition-colors" title="Share Trade">
                                <Share2 size={16} />
                             </button>
                             <button onClick={() => setDeletingTrade(trade)} className="p-2 bg-slate-800 text-slate-300 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors" title="Delete Trade">
                                <Trash2 size={16} />
                             </button>
                           </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Mobile Card Layout */}
              <div className="lg:hidden flex flex-col gap-4">
                 {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <div key={`mob-skeleton-${idx}`} className="p-4 bg-slate-800 animate-pulse rounded-xl border border-slate-700/50">
                         <div className="h-6 bg-slate-700 rounded w-1/4 mb-3"></div>
                         <div className="h-4 bg-slate-700 rounded w-1/2 mb-2"></div>
                         <div className="h-4 bg-slate-700 rounded w-3/4"></div>
                      </div>
                    ))
                 ) : (
                    trades.map((trade) => (
                      <div key={`mob-${trade.id}`} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 shadow-sm flex flex-col gap-3">
                         <div className="flex justify-between items-start border-b border-slate-800 pb-3">
                            <div>
                               <div className="flex items-center gap-2">
                                  <span className="font-bold text-lg text-slate-200">{trade.symbol}</span>
                                  <StatusPill type={trade.side === "BUY" ? "success" : "danger"} className="text-[10px] px-1.5 py-0.5">{trade.side}</StatusPill>
                               </div>
                               <div className="text-xs text-slate-400 mt-0.5">
                                 {trade.securityType} • QTY: {trade.quantity}
                               </div>
                            </div>
                            <div className="text-right">
                               <div className={clsx("font-bold text-lg", getPnlColor(trade.netPnl))}>
                                  {trade.netPnl ? `$${trade.netPnl.toFixed(2)}` : "Open"}
                               </div>
                               <StatusPill type={getStatusType(trade.status)} className="mt-1">
                                 {trade.status?.toUpperCase() || "UNKNOWN"}
                               </StatusPill>
                            </div>
                         </div>
                         
                         <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mt-1">
                            <div className="flex flex-col">
                               <span className="text-slate-500 text-xs font-medium">Entry</span>
                               <span className="text-slate-200 font-medium">${trade.entryPrice?.toFixed(2) || "N/A"}</span>
                            </div>
                            <div className="flex flex-col">
                               <span className="text-slate-500 text-xs font-medium">Exit</span>
                               <span className="text-slate-200 font-medium">${trade.exitPrice?.toFixed(2) || "N/A"}</span>
                            </div>
                            <div className="flex flex-col">
                               <span className="text-slate-500 text-xs font-medium">Strategy</span>
                               <span className="text-slate-300 truncate">{trade.strategy || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setSharingTrade(trade)} className="p-2 bg-slate-800 text-indigo-400 rounded-lg hover:bg-slate-700 transition" title="Share Trade">
                                   <Share2 size={16} /> 
                                </button>
                                <button onClick={() => setSelectedTrade(trade)} className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-emerald-400 rounded-lg hover:bg-slate-700 transition" title="View Chart">
                                   <BarChart2 size={16} /> <span className="text-xs font-medium">Chart</span>
                                </button>
                                <button onClick={() => setDeletingTrade(trade)} className="p-2 bg-slate-800 text-red-400 rounded-lg hover:bg-slate-700 transition" title="Delete Trade">
                                   <Trash2 size={16} />
                                </button>
                            </div>
                         </div>
                      </div>
                    ))
                 )}
              </div>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.total > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              marginTop: "2rem",
              padding: "1rem",
            }}
          >
            {/* Page Size Selector */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <label style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                Records per page:
              </label>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value);
                  setPagination((prev) => ({
                    ...prev,
                    limit: newLimit,
                    page: 1,
                  }));
                  fetchTrades(1, newLimit, sort.orderBy, sort.order);
                }}
                style={{
                  ...styles.select,
                  padding: "0.25rem 0.5rem",
                  fontSize: "0.875rem",
                  minWidth: "80px",
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Pagination Buttons */}
            {pagination.total > pagination.limit && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <button
                  onClick={() => fetchTrades(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    opacity: pagination.page <= 1 ? 0.5 : 1,
                    cursor: pagination.page <= 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                  Page {pagination.page} of {pagination.totalPages} (
                  {pagination.total} total trades)
                </span>
                <button
                  onClick={() => fetchTrades(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    opacity: pagination.page >= pagination.totalPages ? 0.5 : 1,
                    cursor:
                      pagination.page >= pagination.totalPages
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                marginBottom: "1.5rem",
              }}
            >
              New Trade
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={styles.gridCols2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Symbol</label>
                  <input
                    type="text"
                    value={formData.symbol}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        symbol: e.target.value.toUpperCase(),
                      })
                    }
                    style={styles.input}
                    placeholder="AAPL"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Side</label>
                  <select
                    value={formData.side}
                    onChange={(e) =>
                      setFormData({ ...formData, side: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                  </select>
                </div>
              </div>

              <div style={styles.gridCols2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Quantity</label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: e.target.value })
                    }
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Entry Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.entryPrice}
                    onChange={(e) =>
                      setFormData({ ...formData, entryPrice: e.target.value })
                    }
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.gridCols2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Stop Loss</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.stopLoss}
                    onChange={(e) =>
                      setFormData({ ...formData, stopLoss: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Take Profit</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.takeProfit}
                    onChange={(e) =>
                      setFormData({ ...formData, takeProfit: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Strategy</label>
                <input
                  type="text"
                  value={formData.strategy}
                  onChange={(e) =>
                    setFormData({ ...formData, strategy: e.target.value })
                  }
                  style={styles.input}
                  placeholder="e.g., Breakout, Momentum, Mean Reversion"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Exit Strategy</label>
                <select
                  value={formData.exitStrategy}
                  onChange={(e) =>
                    setFormData({ ...formData, exitStrategy: e.target.value })
                  }
                  style={styles.select}
                >
                  <option value="single">Single Exit</option>
                  <option value="multiple">Multiple Exits</option>
                  <option value="trailing">Trailing Stop</option>
                  <option value="time_based">Time Based</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--text-primary)", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={formData.closeImmediately}
                    onChange={(e) =>
                      setFormData({ ...formData, closeImmediately: e.target.checked })
                    }
                  />
                  Close this trade now (already sold/covered)
                </label>
              </div>

              {formData.closeImmediately && (
                <div style={styles.gridCols2}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Exit Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.exitPrice}
                      onChange={(e) =>
                        setFormData({ ...formData, exitPrice: e.target.value })
                      }
                      style={styles.input}
                      required={formData.closeImmediately}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Exit Date/Time</label>
                    <input
                      type="datetime-local"
                      value={formData.exitTime}
                      onChange={(e) =>
                        setFormData({ ...formData, exitTime: e.target.value })
                      }
                      style={styles.input}
                    />
                  </div>
                </div>
              )}

              {formError && (
                <p style={{ color: "#f87171", fontSize: "0.875rem", margin: "0.5rem 0" }}>
                  {formError}
                </p>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setFormError(null); }}
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.button}>
                  Create Trade
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Contextual Chart Modal */}
      {selectedTrade && (
         <ContextualChart trade={selectedTrade} onClose={() => setSelectedTrade(null)} />
      )}

      {/* Social Share Modal */}
      {sharingTrade && (
         <ShareTradeCard trade={sharingTrade} onClose={() => setSharingTrade(null)} />
      )}

      {/* Delete confirmation modal */}
      {deletingTrade && (
        <div style={styles.modal} onClick={() => { setDeletingTrade(null); setDeleteError(null); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, color: "var(--text-primary)" }}>Delete Trade</h2>
            <p style={{ color: "var(--text-secondary)", margin: "1rem 0" }}>
              Delete this {deletingTrade.symbol} {deletingTrade.side} trade? This cannot be undone.
            </p>
            {deleteError && (
              <p style={{ color: "#dc2626", fontSize: "0.875rem" }}>{deleteError}</p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
              <button
                onClick={() => { setDeletingTrade(null); setDeleteError(null); }}
                style={{ ...styles.button, backgroundColor: "white", color: "#374151", border: "1px solid #d1d5db" }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTrade}
                style={{ ...styles.button, backgroundColor: "#dc2626", color: "white" }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB — Add Trade */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-20 right-6 md:hidden bg-emerald-600 hover:bg-emerald-500 text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl shadow-lg z-40"
        aria-label="Add trade"
      >+</button>
    </div>
  );
};

export default Trades;
